/** Calendar dates stay local; never slice UTC ISO strings to choose a working day. */
export function dateKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}
export function shiftDate(key: string, days: number): string {
  const date = new Date(`${key}T12:00:00`)
  date.setDate(date.getDate() + days)
  return dateKey(date)
}
export function monday(date = new Date()): string {
  return shiftDate(dateKey(date), -((date.getDay() + 6) % 7))
}
export function duration(minutes: number): string {
  const value = Math.round(Math.abs(minutes))
  return `${minutes < 0 ? '−' : ''}${Math.floor(value / 60)}h ${String(value % 60).padStart(2, '0')}m`
}
export function monthRange(key: string): [string, string] {
  const date = new Date(`${key}T12:00:00`)
  return [
    dateKey(new Date(date.getFullYear(), date.getMonth(), 1)),
    dateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0)),
  ]
}
export function timer(seconds: number): string {
  const value = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(value / 3600)).padStart(2, '0')}:${String(Math.floor(value / 60) % 60).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`
}
export const localDateTime = (value: string) => {
  const date = new Date(value)
  return `${dateKey(date)}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}
export function zonedDateTime(value: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(value))
  const get = (type: string) => parts.find((part) => part.type === type)?.value
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`
}

/** A wall-clock input has zero instants in a DST gap, or two when the clock repeats.
 * Send an explicit ISO instant; never let the browser's own timezone choose it.
 */
export function zonedInstants(value: string, timeZone: string): string[] {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return []
  const nominal = Date.parse(`${value}:00Z`)
  if (!Number.isFinite(nominal) || new Date(nominal).toISOString().slice(0, 16) !== value) return []
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    second: '2-digit', hourCycle: 'h23',
  })
  const partsAt = (instant: number) => {
    const parts = formatter.formatToParts(new Date(instant))
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value)
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'))
  }
  const offsets = new Set([-36, -24, -12, 0, 12, 24, 36].map((hours) => {
    const seed = nominal + hours * 3600000
    return partsAt(seed) - seed
  }))
  return [...offsets].map((offset) => nominal - offset).filter((instant) => partsAt(instant) === nominal)
    .sort((a, b) => a - b).map((instant) => new Date(instant).toISOString())
}
